import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { configureApi } from '@field-tickets/shared';
import * as Sentry from '@sentry/react';
import { App } from './App';
import { initErrorMonitoring } from './services/errorMonitoring';
import { installComplianceSessionBoundary } from './utils/complianceSession';
import './styles/design-tokens.css';
import './styles/app.css';
import './styles/ticket-modal-v2.css';
import './styles/mobile-v2.css';
// Refinement round — layered after base v2 so they win the cascade
import './styles/chrome-v2.css';
import './styles/modal-v2-polish.css';
import './styles/mobile-v2-polish.css';
import './styles/mobile-ticket-v2.css';
import './styles/notifications.css';
// Directory redesign — one stylesheet per workstream, imported here up front so
// no later workstream has to touch this manifest. Each starts empty and is
// owned by exactly one workstream (see the header comment in each file); they
// load after app.css and override it with doubled selectors where needed,
// because Vite's CSS optimizer reorders rules and source order alone does not
// decide the cascade.
import './styles/home-v2.css';
import './styles/reports-v2.css';
import './styles/catalog-v2.css';
import './styles/directory-v2.css';
import './styles/sales-v2.css';
// kanban-v2.css, kanban-v2-mount.css, board-views-v2.css moved to
// BoardPage.tsx/SiteViewPage.tsx; review-v2.css/review-v2-polish.css moved to
// ReviewQueuePage.tsx — those are page-scoped, code-split into lazy chunks
// (gh-535). Everything left here is shared across multiple lazy routes
// (comments, chips, chrome, mobile action bar) so it must stay in the eager
// bundle to avoid cascade-order gaps on first paint.

// Configure API client for web — uses cookies for auth
initErrorMonitoring();
configureApi({ baseUrl: window.location.origin });

document.addEventListener('wheel', () => {
  const activeElement = document.activeElement;
  if (activeElement instanceof HTMLInputElement && activeElement.type === 'number') {
    activeElement.blur();
  }
}, { passive: true });

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});
installComplianceSessionBoundary(queryClient);

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <Sentry.ErrorBoundary
      fallback={(
        <div className="login-page">
          <h1 style={{ fontSize: 'var(--text-3xl)', marginBottom: 'var(--space-2)' }}>
            Something went wrong
          </h1>
          <p style={{ color: 'var(--color-text-muted)', marginBottom: 'var(--space-6)' }}>
            Reload the page to try again.
          </p>
          <button
            className="login-btn"
            onClick={() => window.location.reload()}
            style={{ border: 'none', cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      )}
    >
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
);
